import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Copy, Sparkle } from "lucide-react";
import type { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../ui/button";
import { Separator } from "../../ui/separator";

import posthog from "posthog-js";
import { AskAi } from "../../../features/ask-ai/ask-ai";

const AssistantToolbarRoot = ({
  children,
  openDelay = 1300,
}: PropsWithChildren<{ openDelay?: number }>) => {
  return <HoverCard openDelay={openDelay}>{children}</HoverCard>;
};

const AssistantToolbarTrigger = ({
  children,
  className,
  asChild = true,
}: PropsWithChildren<{
  asChild?: boolean;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
}>) => {
  return (
    <HoverCardTrigger asChild={asChild} className={className}>
      {children}
    </HoverCardTrigger>
  );
};

const AssistantToolbar = ({
  children,
  align = "center",
  side = "top",
  sideOffset = 10,
}: PropsWithChildren<{
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  sideOffset?: number;
}>) => {
  return (
    <HoverCardContent
      className="bg-background text-primary p-1 shadow-md w-auto"
      align={align}
      side={side}
      sideOffset={sideOffset}
    >
      <div className="flex flex-row items-center">{children}</div>
    </HoverCardContent>
  );
};

const AssistantToolbarCopy = ({ copyText }: { copyText: string }) => {
  const handleOnCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    navigator.clipboard.writeText(copyText);
  };

  return (
    <Button variant="ghost" size="sm" onClick={handleOnCopy}>
      <Copy size={16} />
    </Button>
  );
};

const AssistantToolbarAskAi = ({
  aiPrompt,
  query,
  label,
  type = "internal",
}: {
  aiPrompt: {
    human: string;
    system?: string;
  };
  label?: string;
  query?: string;
  type?: "internal" | "wordware";
}) => {
  const { t } = useTranslation();

  return (
    <AskAi prompt={aiPrompt} query={query} type={type} label={label}>
      {({ askAssistant }) => (
        <Button
          variant="ghost"
          size="sm"
          className="gap-1"
          onClick={(e) => {
            // this button has priority over anything else.
            e.stopPropagation();
            askAssistant();
          }}
        >
          <Sparkle size={16} />
          {label ?? t("ask_ai.label")}
        </Button>
      )}
    </AskAi>
  );
};

const AssistantToolbarSeparator = () => {
  return <Separator orientation="vertical" className="h-6 mx-0.5" />;
};

export {
  AssistantToolbarRoot,
  AssistantToolbarTrigger,
  AssistantToolbarCopy,
  AssistantToolbarAskAi,
  AssistantToolbarSeparator,
  AssistantToolbar,
};
